import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../state/auth';
import { api } from '../api/client';
import { useT } from '../i18n';

export default function Login() {
  const t = useT();
  const { login, applyToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);

  // Auto-login path: server redirects /api/auth/magic/:token here with
  // ?autoToken=… after consuming the magic link. We apply it and bounce to
  // /account. ?magicError=expired shows a friendly message.
  useEffect(() => {
    const autoToken = params.get('autoToken');
    const magicError = params.get('magicError');
    if (magicError) {
      setError(t.magicLinkExpired);
      return;
    }
    if (!autoToken) return;
    setAutoLoggingIn(true);
    (async () => {
      try {
        await applyToken(autoToken);
        navigate('/account', { replace: true });
      } catch (e) {
        setError(e.message);
        setAutoLoggingIn(false);
      }
    })();
  }, [params, applyToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Forgot password → email a single-use login link. The endpoint always
  // responds 200 (no email enumeration), so show the same notice regardless.
  const handleForgot = async () => {
    setError('');
    setNotice('');
    if (!email) { setError(t.enterEmailFirst); return; }
    try {
      await api.post('/api/auth/forgot-password', { email });
    } catch { /* generic notice below either way */ }
    setNotice(t.resetLinkSent);
  };

  if (autoLoggingIn) {
    return (
      <div className="auth-page">
        <div className="auth-form form-zone">
          <p>{t.autoLoginInProgress}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="auth-form form-zone" onSubmit={handleSubmit}>
        <h2 className="zone-title">{t.login}</h2>

        {error && <p className="auth-error">{error}</p>}
        {notice && <p className="auth-notice" style={{ color: '#16a34a' }}>{notice}</p>}

        <label className="field">
          <span className="field-label">{t.email}</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">{t.password}</span>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>

        <button type="submit" className="submit-btn" disabled={busy}>
          {t.loginBtn}
        </button>

        <p className="auth-switch">
          <button
            type="button"
            onClick={handleForgot}
            style={{ background: 'none', border: 'none', padding: 0, color: '#6366f1', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
          >
            {t.forgotPassword}
          </button>
        </p>

        <p className="auth-switch">
          {t.noAccount} <Link to="/register">{t.register}</Link>
        </p>
      </form>
    </div>
  );
}
